import * as authApi from "./authApi";
import {
  AuthSession,
  AuthTokenResponse,
  isSessionExpired,
  sessionFromTokenResponse,
} from "./session";

/**
 * The one place that hands out a usable id token.
 *
 * Every caller that needs to authenticate a request (Apollo, startup, future
 * REST calls) goes through `getValidIdToken()`. It returns the current token
 * when it's fresh, refreshes it when it's stale, and returns `null` when the
 * user is logged out. Concurrent callers share a single refresh request.
 *
 * If the server rejects the refresh token, `onExpired` is called (which logs
 * the user out with a message) and `null` is returned so the request can
 * continue anonymously. Network failures are thrown, not treated as logout.
 */

export interface TokenProviderDeps {
  getSession: () => AuthSession | null;
  onRefreshed: (session: AuthSession) => void;
  onExpired: () => void;
  refresh?: (session: AuthSession) => Promise<AuthTokenResponse>;
  now?: () => number;
}

export interface TokenProvider {
  getValidIdToken: () => Promise<string | null>;
  /**
   * Refresh even if the token looks fresh, e.g. after the API rejected it.
   * Pass the token that was rejected: if the session has already moved on to
   * a newer token, that one is returned instead of refreshing again.
   */
  forceRefresh: (rejectedToken?: string) => Promise<string | null>;
}

export function createTokenProvider(deps: TokenProviderDeps): TokenProvider {
  const refresh = deps.refresh ?? authApi.refresh;
  const now = deps.now ?? Date.now;

  let inFlight: Promise<AuthSession | null> | null = null;

  // The user may log out (or log in as someone else) while a refresh is in
  // flight; only act on the result if it's still the same session.
  const isCurrent = (session: AuthSession) =>
    deps.getSession()?.refreshToken === session.refreshToken;

  function refreshOnce(session: AuthSession): Promise<AuthSession | null> {
    if (!inFlight) {
      inFlight = (async () => {
        try {
          const tokens = await refresh(session);
          const next = sessionFromTokenResponse(tokens, session, now());
          if (!isCurrent(session)) return deps.getSession();
          deps.onRefreshed(next);
          return next;
        } catch (error) {
          if (authApi.isRejectedByServer(error)) {
            if (isCurrent(session)) deps.onExpired();
            return null;
          }
          throw error;
        } finally {
          inFlight = null;
        }
      })();
    }
    return inFlight;
  }

  return {
    async getValidIdToken() {
      const session = deps.getSession();
      if (!session) return null;
      if (!isSessionExpired(session, now())) return session.idToken;
      return (await refreshOnce(session))?.idToken ?? null;
    },

    async forceRefresh(rejectedToken) {
      const session = deps.getSession();
      if (!session) return null;
      if (rejectedToken && session.idToken !== rejectedToken) {
        return session.idToken;
      }
      return (await refreshOnce(session))?.idToken ?? null;
    },
  };
}
